import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  KeyboardAvoidingView,
  SafeAreaView,
  Image,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { GooglePlacesAutocomplete } from 'react-google-places-autocomplete';
import API_PATHS from '../../apiCalls/ApiPath';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { loginActions } from '../../slices/authSlice';


const GOOGLE_PLACES_API_KEY = "AIzaSyDcbIVmQj3eXgV-qmKuZXS8IdU0CZLOQNc";

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const AddNewAddress = (props) => {
  const { navigation } = props || {};

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_PLACES_API_KEY,
    libraries: ['places']
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [houseNo, setHouseNo] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");
  const [addressName, setAddressName] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [otherAddress, setOtherAddress] = useState("");
  const [region, setRegion] = useState({
    latitude: 51.5074,
    longitude: 0.1278,
    latitudeDelta: 0.0922,
    longitudeDelta: 0.0421,
  });
  const [mapMarker, setMapMarker] = useState(null);
  const [saveError, setSaveError] = useState("");
  const dispatch = useDispatch();

  const debounceTimeout = useRef(null);
  const mapRef = useRef(null);
  const placesServiceRef = useRef(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setRegion({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            latitudeDelta: 0.0922,
            longitudeDelta: 0.0421,
          });
          setMapMarker({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (isLoaded && window.google) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement("div"));
    }
  }, [isLoaded]);

  const fetchAddressPredictions = async (text) => {
    if (!text || text.length < 3 || !isLoaded || !window.google) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    try {
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        {
          input: text,
          types: ['geocode', 'establishment'],
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
          } else {
            setPredictions([]);
          }
          setLoading(false);
        }
      );
    } catch (error) {
      console.error("Error fetching address predictions:", error);
      setLoading(false);
      setPredictions([]);
    }
  };

  const handleSearchChange = (text) => {
    setSearchQuery(text);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchAddressPredictions(text);
    }, 300);
  };

  const handleSelectAddress = (placeId, description) => {
    if (!isLoaded || !window.google || !placesServiceRef.current) return;

    try {
      placesServiceRef.current.getDetails(
        { placeId: placeId, fields: ['address_components', 'geometry', 'formatted_address', 'name'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const { address_components, geometry, formatted_address } = place;

            console.log(formatted_address);

            let streetNumber = "";
            let postalCode = "";
            let cityName = "";
            let area = "";

            if (address_components) {
              address_components.forEach((component) => {
                if (component.types.includes("street_number")) {
                  streetNumber = component.long_name;
                }
                if (component.types.includes("postal_code")) {
                  postalCode = component.long_name;
                }
                if (component.types.includes("locality")) {
                  cityName = component.long_name;
                }
                if (component.types.includes("route")) {
                  area = component.long_name;
                }
              });
            }

            setHouseNo(streetNumber);
            setZipCode(postalCode);
            setCity(cityName);
            setFullAddress(streetNumber + ", " + formatted_address || description);
            setSearchQuery(formatted_address || description);
            setPredictions([]);

            if (geometry && geometry.location) {
              const lat = geometry.location.lat();
              const lng = geometry.location.lng();

              setRegion({
                latitude: lat,
                longitude: lng,
                latitudeDelta: 0.0922,
                longitudeDelta: 0.0421,
              });

              setMapMarker({
                latitude: lat,
                longitude: lng,
              });

              if (mapRef.current) {
                mapRef.current.panTo({ lat, lng });
              }
            }
          } else {
            console.error("Error fetching place details:", status);
          }
        }
      );
    } catch (error) {
      console.error("Error handling place selection:", error);
    }
  };

  const saveAddress = async () => {
    setSaveError("");

    if (!fullAddress || !city) {
      setSaveError("Please select a valid address");
      toast.error("Please select a valid address");
      return;
    }

    if (!addressName) {
      setSaveError("Please provide a name for this address");
      toast.error("Please provide a name for this address");
      return;
    }

    try {

      // setTimeout(() => {
      //   toast.success("Address saved successfully!");
      //   if (navigation && navigation.goBack) {
      //     navigation.goBack();
      //   }
      // }, 1000);

      const token = await AsyncStorage.getItem("token");
      const clientToken = await AsyncStorage.getItem("clientToken");
      if (!token) {
        setSaveError("Authentication token not found");
        toast.error("Authentication token not found. Please login again.");
        return;
      }

      const response = await fetch(`${API_PATHS.BASE_URL}${API_PATHS.CREATE_ADDRESS}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${clientToken || token}`,
        },
        body: JSON.stringify({
          latitude: region.latitude,
          longitude: region.longitude,
          area: otherAddress ? fullAddress + ", " + otherAddress : fullAddress,
          zipcode: zipCode || "",
          city: city || "",
          houseno: "",
          tag: addressName,
        }),
      });

      if (!response.ok) {
        const apiUrl = encodeURIComponent(`${API_PATHS.BASE_URL}${API_PATHS.CREATE_ADDRESS}`);

        const proxyResponse = await fetch(`${apiUrl}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${clientToken || token}`,
          },
          body: JSON.stringify({
            latitude: region.latitude,
            longitude: region.longitude,
            area: fullAddress,
            zipcode: zipCode || "",
            city: city || "",
            houseno: "",
            tag: addressName,
          }),
        });

        const data = await proxyResponse.json();

        if (data.Success == 1) {
          toast.success("Address saved successfully!");
          if (navigation && navigation.goBack) {
            navigation.goBack();
          }
        } else {
          setSaveError(data.Message || "Unknown error");
          toast.error(`Failed to save address: ${data.Message || "Unknown error"}`);
        }
      } else {
        const data = await response.json();

        if (data.Success == 1) {
          toast.success("Address saved successfully!");
          if (navigation && navigation.goBack) {
            navigation.goBack();
          }
        } else {
          setSaveError(data.Message || "Unknown error");
          toast.error(`Failed to save address: ${data.Message || "Unknown error"}`);
        }
      }


    } catch (error) {
      console.error("Error saving address:", error);
      setSaveError(error.message || "Failed to save address");
      toast.error("Failed to save address. Please try again.");
    }
  };

  const handleMapClick = (e) => {
    const newMarker = {
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
    };

    setMapMarker(newMarker);
    setRegion({
      ...region,
      latitude: newMarker.latitude,
      longitude: newMarker.longitude,
    });

    reverseGeocode(newMarker.latitude, newMarker.longitude);
  };

  const reverseGeocode = async (lat, lng) => {
    if (!isLoaded || !window.google) return;

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const result = results[0];
          setFullAddress(result.formatted_address);
          setSearchQuery(result.formatted_address);

          const addressComponents = result.address_components;
          let streetNumber = "";
          let postalCode = "";
          let cityName = "";
          let area = "";

          addressComponents.forEach((component) => {
            if (component.types.includes("street_number")) {
              streetNumber = component.long_name;
            }
            if (component.types.includes("postal_code")) {
              postalCode = component.long_name;
            }
            if (component.types.includes("locality")) {
              cityName = component.long_name;
            }
            if (component.types.includes("route")) {
              area = component.long_name;
            }
          });

          setHouseNo(streetNumber);
          setZipCode(postalCode);
          setCity(cityName);
        }
      });
    } catch (error) {
      console.error("Error reverse geocoding:", error);
    }
  };

  const renderMap = () => {
    if (!isLoaded) {
      return (
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <ActivityIndicator size="large" color="#A6CE39" />
          <Text style={{ marginTop: 10 }}>Loading Map...</Text>
        </View>
      );
    }

    return (
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={{ lat: region.latitude, lng: region.longitude }}
        zoom={14}
        onClick={handleMapClick}
        onLoad={map => {
          mapRef.current = map;
        }}
      >
        {mapMarker && (
          <Marker
            position={{ lat: mapMarker.latitude, lng: mapMarker.longitude }}
          />
        )}
      </GoogleMap>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        <ToastContainer />
        <KeyboardAvoidingView behavior="height" style={styles.container}>
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => (navigation && navigation.goBack ? navigation.goBack() : null)}
            >
              <Text style={styles.backButtonText}>←</Text>
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Add New Address</Text>
          </View>

          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder="Search Area"
              value={searchQuery}
              onChangeText={handleSearchChange}
            />
            {loading && <ActivityIndicator style={styles.loadingIndicator} color="#fff" />}
          </View>

          {predictions.length > 0 && (
            <ScrollView style={styles.predictionsContainer}>
              {predictions.map((prediction) => (
                <TouchableOpacity
                  key={prediction.place_id}
                  style={styles.predictionItem}
                  onPress={() => handleSelectAddress(prediction.place_id, prediction.description)}
                >
                  <Text style={styles.predictionText}>{prediction.description}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}

          <View style={styles.mapContainer}>
            {renderMap()}
          </View>

          <View style={styles.formContainer}>
            <TextInput style={styles.input} placeholder="Address Line 1*" value={fullAddress} onChangeText={setFullAddress} />
            <TextInput style={styles.input} placeholder="Address Line 2" value={otherAddress} onChangeText={setOtherAddress} />

            {/* <TextInput style={styles.input} placeholder="House No" value={houseNo} onChangeText={setHouseNo} /> */}
            <TextInput style={styles.input} placeholder="Zip Code" value={zipCode} onChangeText={setZipCode} />
            <TextInput style={styles.input} placeholder="City" value={city} onChangeText={setCity} />
            <TextInput
              style={styles.input}
              placeholder="Name it like HOME, MUSIC"
              value={addressName}
              onChangeText={setAddressName}
            />

            {saveError ? <Text style={styles.errorText}>{saveError}</Text> : null}

            <TouchableOpacity style={styles.saveButton} onPress={saveAddress}>
              <Text style={styles.saveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#A6CE39",
    paddingVertical: 15,
    paddingHorizontal: 10,
  },
  backButton: {
    padding: 5,
  },
  backButtonText: {
    fontSize: 24,
    color: "white",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    fontWeight: "bold",
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "white",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
    alignSelf: "center",
    marginLeft: 10,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#A6CE39",
    paddingHorizontal: 10,
    paddingBottom: 15,
  },
  searchInput: {
    flex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: 20,
    paddingHorizontal: 15,
    paddingVertical: 10,
    fontSize: 16,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
  loadingIndicator: {
    position: "absolute",
    right: 20,
  },
  predictionsContainer: {
    position: "absolute",
    top: 110,
    left: 10,
    right: 10,
    backgroundColor: "white",
    borderRadius: 5,
    zIndex: 1000,
    maxHeight: 200,
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  predictionItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  predictionText: {
    fontSize: 14,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
  mapContainer: {
    height: 400,
    width: "100%",
    position: "relative",
    marginBottom: 15,
  },
  formContainer: {
    padding: 15,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 5,
    padding: 15,
    marginBottom: 15,
    fontSize: 16,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
  errorText: {
    color: 'red',
    marginBottom: 10,
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
  saveButton: {
    backgroundColor: "#a4d65e",
    padding: 15,
    borderRadius: 5,
    alignItems: "center",
    marginTop: 10,
  },
  saveButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "bold",
    fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    fontStyle: "normal",
  },
});

export default AddNewAddress;